import {
  ClientsInput,
  GetFlowchartCampaignsInDatesQuery,
  GetFlowchartCampaignsInDatesQueryVariables,
  GetStoredQueryQuery,
  GetStoredQueryQueryVariables,
  MetricsInfo,
  queryInput,
  StartQueryMutation,
  StartQueryMutationVariables,
  StoreQueriesV2Mutation,
  StoreQueriesV2MutationVariables,
  UserQuery,
  filtertype,
  datatype,
} from 'API';
import { API, Storage } from 'aws-amplify';
import { startQuery, storeQueriesV2 } from 'graphql/mutations';
import { getFlowchartCampaignsInDates, getStoredQuery } from 'graphql/queries';
import { useCustomReportContext } from 'context/CustomReportContext';

// Type for new filter values when duplicating
export type DuplicateQueryFilters = {
  filterType: 'date' | 'campaigns';
  // For date filter - need client info to fetch campaigns in date range
  startDate?: string;
  endDate?: string;
  clientCode?: string;
  clientCountry?: string;
  // For campaign filter
  campaignIds?: string[];
};

export const useGetStoredQuery = (): readonly [(position: string) => Promise<UserQuery | null>] => {
  const { activeCustomReport } = useCustomReportContext();
  const fetchCustomReportQuery = async (position: string) => {
    console.log('KURVA ISUSOVA', position);
    try {
      const response = (await API.graphql({
        query: getStoredQuery,
        variables: {
          campaign_id: activeCustomReport?.CustomReportID.toString(),
          position: position,
        } as GetStoredQueryQueryVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetStoredQueryQuery };
      const r = response.data.getStoredQuery;
      console.log('KURVA ISUSOVA', r);
      if (r) {
        return r;
      } else {
        return null;
      }
    } catch (e) {
      console.error(e);
      return null;
    }
  };
  return [fetchCustomReportQuery];
};

export const useSaveCustomReportQuery = (): readonly [
  (
    position: string,
    query_id: string,
    query_id_cc: string,
    title: string,
    metrics?: MetricsInfo[],
    query_id_benchmark?: string,
    hideTotal?: boolean,
    customReportId?: number,
  ) => Promise<string>,
] => {
  const { activeCustomReport } = useCustomReportContext();
  const saveCustomReportQuery = async (
    position: string,
    query_id: string,
    query_id_cc: string,
    title: string,
    metrics?: MetricsInfo[],
    query_id_benchmark?: string,
    hideTotal?: boolean,
    customReportId?: number,
  ) => {
    const idToUse = customReportId || activeCustomReport?.CustomReportID;
    try {
      const response = (await API.graphql({
        query: storeQueriesV2,
        variables: {
          campaign_id: idToUse?.toString(),
          position: position,
          query_id: query_id,
          query_id_cc: query_id_cc,
          ...(query_id_benchmark && { query_id_benchmark: query_id_benchmark }),
          ...(metrics && { metrics: metrics }),
          ...(hideTotal !== undefined && { hideTotal: hideTotal }),
          title: title,
          // metrics?: Array<MetricsInfo | null> | null
        } as StoreQueriesV2MutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: StoreQueriesV2Mutation };
      const r = response.data.storeQueriesV2;
      if (r) {
        if (r.status === '200') {
          return 'success' as string;
        } else {
          return 'error';
        }
      } else {
        return 'error';
      }
    } catch (e) {
      console.log('ERROR SAVING CUSTOM REPORT QUERY', e);
      return 'ERROR SAVING CUSTOM REPORT QUERY';
    }
  };
  return [saveCustomReportQuery];
};

const { REACT_APP_USER_FILES_S3_BUCKET } = process.env;

/**
 * Helper function to fetch campaigns in a date range (same logic as useDateReportsCampaigns)
 * Exported for use in validation before creating custom reports
 */
export const fetchCampaignsInDateRange = async (
  clientCode: string,
  clientCountry: string,
  from: string,
  to: string,
): Promise<string[] | null> => {
  try {
    const clientCodes = [
      {
        ClientCode: clientCode,
        ClientCountry: clientCountry,
      } as ClientsInput,
    ];

    const response = (await API.graphql({
      query: getFlowchartCampaignsInDates,
      variables: {
        clientCodes: clientCodes,
        from: from,
        to: to,
      } as GetFlowchartCampaignsInDatesQueryVariables,
      authMode: 'AMAZON_COGNITO_USER_POOLS',
    })) as { data: GetFlowchartCampaignsInDatesQuery };

    const responseData = response.data.getFlowchartCampaignsInDates;
    if (!responseData) {
      return null;
    }

    const campaigns = responseData
      .map((item) => item?.campaignId.toString())
      .filter((id): id is string => id !== null && id !== undefined);

    return campaigns;
  } catch (e) {
    console.error('Error fetching campaigns in date range:', e);
    return null;
  }
};

/**
 * Hook to duplicate a query with updated filters for a new custom report
 * Uses same filter logic as getQueryFilters from queryFiltersMethod.tsx and CustomReportContext
 *
 * For date-based reports:
 * 1. Fetches campaigns in the date range using getFlowchartCampaignsInDates
 * 2. Adds both date filter AND campaign_id filter (same as how CustomReportContext works)
 *
 * For campaign-based reports:
 * 1. Adds campaign_id filter with IN/EQUAL based on count
 */
export const useDuplicateQuery = (): readonly [
  (oldQueryId: string, newFilters: DuplicateQueryFilters) => Promise<string | null>,
] => {
  const duplicateQuery = async (oldQueryId: string, newFilters: DuplicateQueryFilters): Promise<string | null> => {
    if (!oldQueryId) return null;

    try {
      // Step 1: Fetch original query definition from S3
      const link = await Storage.get(`${oldQueryId}.json`, {
        level: 'protected',
        identityId: 'queries',
        region: 'eu-west-1',
        bucket: REACT_APP_USER_FILES_S3_BUCKET,
      });

      const response = await fetch(`${link}`);
      if (!response.ok) {
        console.error('Failed to fetch query definition from S3:', response.status);
        return null;
      }

      const queryDefinition = (await response.json()) as queryInput & { queryid?: string };

      // Remove queryid if present (it's from the old query)
      delete queryDefinition.queryid;

      // Step 2: Remove old campaign_id and date filters (keep other filters like custom conversions, etc.)
      const existingFilters =
        queryDefinition.query.filters?.filter((f) => {
          if (!f) return false;
          const col = f.column?.toLowerCase();
          return col !== 'campaign_id' && col !== 'date';
        }) || [];

      // Step 3: Add new filters based on filter type
      if (newFilters.filterType === 'date') {
        // Date-based report: need to fetch campaigns in the date range first
        // This is the same logic as CustomReportContext uses via useDateReportsCampaigns
        if (newFilters.startDate && newFilters.endDate && newFilters.clientCode && newFilters.clientCountry) {
          // Fetch campaigns that fall within the date range
          const campaignsInRange = await fetchCampaignsInDateRange(
            newFilters.clientCode,
            newFilters.clientCountry,
            newFilters.startDate,
            newFilters.endDate,
          );

          // Add date filter
          existingFilters.push({
            column: 'date',
            filter: {
              filtertype: filtertype.BETWEEN,
              datatype: datatype.DATE,
              from: newFilters.startDate,
              to: newFilters.endDate,
            },
          });

          // Update date property in queryInput
          queryDefinition.date = {
            from: newFilters.startDate,
            to: newFilters.endDate,
          };

          // Also add campaign_id filter if we found campaigns (same as getQueryFilters does)
          if (campaignsInRange && campaignsInRange.length > 0) {
            if (campaignsInRange.length > 1) {
              existingFilters.push({
                column: 'campaign_id',
                filter: {
                  filtertype: filtertype.IN,
                  datatype: datatype.NUMBER,
                  list: campaignsInRange,
                },
              });
            } else {
              existingFilters.push({
                column: 'campaign_id',
                filter: {
                  filtertype: filtertype.EQUAL,
                  datatype: datatype.NUMBER,
                  value: campaignsInRange[0],
                },
              });
            }
          }
        }
      } else if (newFilters.filterType === 'campaigns' && newFilters.campaignIds?.length) {
        // Campaign-based report: use same logic as getQueryFilters
        if (newFilters.campaignIds.length > 1) {
          existingFilters.push({
            column: 'campaign_id',
            filter: {
              filtertype: filtertype.IN,
              datatype: datatype.NUMBER,
              list: newFilters.campaignIds,
            },
          });
        } else {
          existingFilters.push({
            column: 'campaign_id',
            filter: {
              filtertype: filtertype.EQUAL,
              datatype: datatype.NUMBER,
              value: newFilters.campaignIds[0],
            },
          });
        }
      }

      // Update the query definition with new filters
      queryDefinition.query.filters = existingFilters;

      // Step 4: Start new query with modified definition
      const startQueryResponse = (await API.graphql({
        query: startQuery,
        variables: { query: queryDefinition } as StartQueryMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: StartQueryMutation };

      const newQueryId = startQueryResponse.data.startQuery?.id;

      if (!newQueryId) {
        console.error('Failed to start new query - no ID returned');
        return null;
      }

      console.log('Successfully duplicated query. Old ID:', oldQueryId, 'New ID:', newQueryId);
      return newQueryId;
    } catch (e) {
      console.error('Error duplicating query:', e);
      return null;
    }
  };

  return [duplicateQuery];
};
